import React, { useState } from "react";
import SoldProducts from "./SoldProducts";
import ReturnProducts from "./ReturnProducts";
import { ChevronLeftCircle } from "lucide-react";
import { OfflineItemService } from "../../../../../../services/api/OfflineOrder/offline.service";
import medicineIcon from "../../../../../../assets/images/medicineIcon.png";
import { useAuth } from "../../../../../../context/AuthContext";
import { CreateOfflineReturnPayload } from "../../../../../../services/api/OfflineOrder/types";
import toast from "react-hot-toast";
import { OfflineOrderDetailsData } from "../types";
import { OfflineProductDetail } from "../types";

export interface SelectedProduct {
  productId: string;
  noOfItems: number;
  inwardStockId: string;
  spPerTablet: number;
  name: string;
  image: string;

}

export interface OrderedProduct {
  id: string;
  product_id: string;
  inwardStockId: string;
  product_name: string;
  manufacturer_name: string;
  product_image: string;
  mrp: number;
  tabletsPerStripe: number;
  spPerTablet: number;
  quantity: number;
  hsn_number: string;
  discount: number;
  cgst: number;
  sgst: number;
}


interface ReturnMainProps {
  orderDetails: OfflineOrderDetailsData;
  onBack: () => void;
}

const ReturnMain: React.FC<ReturnMainProps> = ({ orderDetails, onBack }) => {
  const [returnProducts, setReturnProducts] = useState<SelectedProduct[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [selectedProducts, setSelectedProducts] = useState([]);
  // const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  // const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(null);
  // const [searchText, setSearchText] = useState("");
  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";

  const orderedProducts: OrderedProduct[] = orderDetails.productDetails.map((product: OfflineProductDetail) => ({
    id: product?.productId,
    product_id: product?.productId,
    inwardStockId: product?.inwardStockId,
    product_name: product?.productName,
    manufacturer_name:
      product?.manufacturer || "Unknown Manufacturer",
    product_image: product?.productImage || medicineIcon,
    mrp:
      Number(product?.productMrp) /
      (product?.tabletsPerStripe || 1),
    tabletsPerStripe: product?.tabletsPerStripe || 1,
    spPerTablet: Number(product.totalPriceOfProduct) / (product?.productsItemsCount || 1),
    quantity: product?.productsItemsCount,
    hsn_number: product?.hsnNumber || "",
    discount: parseFloat(product?.sellingDiscount) || 0,
    cgst: parseFloat(product?.cgst) || 0,
    sgst: parseFloat(product?.sgst) || 0,
  }));

  const handleAddProduct = (inwardStockId: string) => {
    console.log(inwardStockId);
    const product = orderedProducts.find((p) => p.inwardStockId === inwardStockId);
    if (product) {
      const existingProduct = returnProducts.find(
        (p) => p.inwardStockId === product.inwardStockId
      );

      if (existingProduct) {
        handleUpdateQuantity(existingProduct.productId, 1);
      } else {
        const newReturnProduct: SelectedProduct = {
          ...product,
          productId: product.product_id,
          noOfItems: 1,
          inwardStockId: product.inwardStockId,
          spPerTablet: Number(product.spPerTablet),
          name: product.product_name,
          image: product.product_image,
        };
        setReturnProducts((prev) => [...prev, newReturnProduct]);
      }
    }
  };

  const handleCalculateExtraDiscount=()=>{
     const total = orderedProducts.reduce(
      (total, product) => total + product.spPerTablet * product.quantity,
      0
    );
    return Math.round(total-Number(orderDetails.totalAmount));
  }

  const handleUpdateQuantity = (inwardStockId: string, change: number) => {
    console.log(inwardStockId, change);
    setReturnProducts((prev) =>
      prev.map((product) => {
        if (product.inwardStockId === inwardStockId) {
          const orderedProduct = orderedProducts.find(
            (p) => p.inwardStockId === product.inwardStockId
          );
          console.log(orderedProduct);
          const maxQuantity = orderedProduct ? orderedProduct.quantity : 1;

          return {
            ...product,
            noOfItems: Math.max(
              1,
              Math.min((product.noOfItems || 1) + change, maxQuantity)
            ),
          };
        }
        return product;
      })
    );
  };


  const handleRemoveProduct = (inwardStockId: string) => {
    setReturnProducts((prev) =>
      prev.filter((product) => product.inwardStockId !== inwardStockId)
    );
  };

  const calculateReturnTotal = () => {
    const total = returnProducts.reduce(
      (total, product) => total + product.spPerTablet * product.noOfItems,
      0
    );
    if(total>Number(orderDetails.totalAmount)){
      const extraDiscount = handleCalculateExtraDiscount();
      return Math.round(total - extraDiscount);
    }
    return Math.round(total);
  };

  const handleSubmitReturn = async () => {
    // Calculate return total
    const returnTotal = calculateReturnTotal();
    const orderTotal = Number(orderDetails.totalAmount);

    // Validate return amount doesn't exceed order amount
    if (returnTotal > orderTotal) {
      toast.error("Return amount cannot exceed the original order amount");
      return;
    }

    // Validate at least one product is being returned
    if (returnProducts.length === 0) {
      toast.error("Please add at least one product to return");
      return;
    }

    setIsLoading(true);
    try {
      // Prepare the payload
      const payload: CreateOfflineReturnPayload = {
        clinicId: clinicId,
        offlineOrderId: orderDetails.orderId,
        returnOrderTotal: returnTotal,
        totalReturnItems: returnProducts.reduce(
          (total, product) => total + product.noOfItems,
          0
        ),
        products: returnProducts.map((product) => ({
          productId: product.productId,
          noOfItems: product.noOfItems,
          inwardStockId: product.inwardStockId,
        })),
      };

      // Validate all required fields are present
      if (
        !payload.clinicId ||
        !payload.offlineOrderId ||
        payload.products.some((p) => !p.inwardStockId)
      ) {
        throw new Error("Missing required fields in return payload");
      }

      const response = await OfflineItemService.returnOfflineOrder(payload);
      console.log(response);
      toast.success("Return submitted successfully");
      onBack();
    } catch (error) {
      console.error("Error submitting return:", error);
      toast.error(error instanceof Error ? error.message : "Failed to submit return");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={onBack}
        className="flex items-center gap-2 mb-4 text-black hover:text-blue-700"
      >
        <ChevronLeftCircle size={20} />
        Back to Order Details
      </button>
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg p-6 mb-6 border-t-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div className="grid grid-cols-4 gap-8 flex-1">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Patient Name
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {orderDetails.patientName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Mobile Number
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {orderDetails.patientMobileNumber}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Invoice Number
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {orderDetails.invoiceNumber}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Order Amount
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    ₹{orderDetails.totalAmount}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Return Amount
                  </label>
                  <p
                    className={`text-lg font-semibold ${calculateReturnTotal() > Number(orderDetails.totalAmount)
                        ? "text-red-600"
                        : "text-gray-900"
                      }`}
                  >
                    ₹{calculateReturnTotal().toFixed(2)}
                    {calculateReturnTotal() > Number(orderDetails.totalAmount) && (
                      <span className="text-xs text-red-600 block">
                        (Exceeds order amount)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <button
                className="ml-6 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                onClick={handleSubmitReturn}
              >
                Update Inventory
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <SoldProducts
              products={orderedProducts} // Pass the ordered products directly
              onAddProduct={handleAddProduct}
              maxQuantities={orderedProducts.reduce((acc, product) => {
                acc[product.id] = product.quantity; // Ordered quantity is the max
                return acc;
              }, {} as Record<string, number>)}
            />
            <ReturnProducts
              returnProducts={returnProducts}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveProduct={handleRemoveProduct}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ReturnMain;
